<template>
  <StatCard
    title="How late things are"
    subtitle="Open tasks past their due date"
    :loading="loading"
    :empty="isEmpty"
    as-of-now
    empty-title="Nothing overdue"
    empty-hint="Every open task is still within its due date."
  >
    <div ref="chartEl" class="w-full h-64"></div>
  </StatCard>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue'
import StatCard from './StatCard.vue'

const props = defineProps({
  data: { type: Array, default: () => [] },
  loading: { type: Boolean, default: false },
})

// Deepening red with age. The colour is the message: the right-hand end of
// this chart is where the real problems are.
const SHADES = ['#fca5a5', '#f87171', '#ef4444', '#dc2626', '#b91c1c']

const chartEl = ref(null)
let chart = null

const isEmpty = computed(() => !props.loading && props.data.every(d => d.count === 0))

function render() {
  if (!chartEl.value || isEmpty.value) return
  // Highcharts arrives from a CDN via layouts/_highcharts.html.erb. If that is
  // blocked or still in flight, degrade to an empty chart area rather than
  // throwing and taking the rest of the page down with it.
  if (typeof Highcharts === 'undefined') return
  if (chart) { chart.destroy(); chart = null }

  chart = Highcharts.chart(chartEl.value, {
    chart: { type: 'column', height: 256, spacing: [16, 16, 8, 8] },
    xAxis: { categories: props.data.map(d => d.bucket), lineWidth: 0, tickLength: 0 },
    yAxis: { min: 0, allowDecimals: false, title: { text: null } },
    legend: { enabled: false },
    tooltip: { pointFormat: '<b>{point.y}</b> tasks' },
    plotOptions: { column: { borderWidth: 0, borderRadius: 3, pointPadding: 0.08 } },
    series: [{
      name: 'Tasks',
      data: props.data.map((d, i) => ({ y: d.count, color: SHADES[i] || SHADES[SHADES.length - 1] })),
    }],
  })
}

watch(() => props.data, () => nextTick(render), { deep: true })
onMounted(() => { if (!isEmpty.value) nextTick(render) })
onUnmounted(() => { if (chart) { chart.destroy(); chart = null } })
</script>
